import React from 'react';
import { colors, spacing, typography, borderRadius, shadows } from '../../theme/tokens';

/**
 * StatCard Component
 *
 * A card component for displaying statistics/metrics with optional icon and status indicator.
 *
 * @param {string} label - Label text for the statistic
 * @param {string|number} value - The main statistic value
 * @param {string} icon - Optional icon class (e.g., 'fas fa-shield-alt')
 * @param {string} status - Optional status: 'success' | 'warning' | 'error' | 'info'
 * @param {string} subtitle - Optional subtitle/description text
 * @param {boolean} loading - Whether the card is in loading state
 * @param {function} onClick - Optional click handler
 */
const StatCard = ({
  label,
  value,
  icon,
  status,
  subtitle,
  loading = false,
  onClick,
  className = '',
}) => {
  const statusColors = {
    success: colors.status.success,
    warning: colors.status.warning,
    error: colors.status.error,
    info: colors.status.info,
  };

  const borderColor = status ? statusColors[status] : colors.border.default;

  return (
    <div
      style={{
        backgroundColor: colors.bg.primary,
        border: `2px solid ${borderColor}`,
        borderRadius: borderRadius.lg,
        padding: spacing.xl,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        boxShadow: shadows.sm,
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = shadows.md;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = shadows.sm;
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}
      className={className}
    >
      {/* Icon and Label Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.medium,
            color: colors.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {label}
        </div>
        {icon && (
          <i
            className={icon}
            style={{
              fontSize: typography.fontSize.xl,
              color: borderColor,
            }}
          />
        )}
      </div>

      {/* Value */}
      {loading ? (
        <div
          style={{
            height: '36px',
            backgroundColor: colors.bg.secondary,
            borderRadius: borderRadius.sm,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ) : (
        <div
          style={{
            fontSize: typography.fontSize.xxxl,
            fontWeight: typography.fontWeight.bold,
            color: colors.text.primary,
            lineHeight: typography.lineHeight.tight,
          }}
        >
          {value}
        </div>
      )}

      {/* Subtitle */}
      {subtitle && (
        <div
          style={{
            fontSize: typography.fontSize.xs,
            color: colors.text.tertiary,
            marginTop: spacing.xs,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
};

export default StatCard;
